import { notification } from 'antd';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { NumberTypeEnum, useworkspaceAuthMeV2LazyQuery } from '@/types/generated/graphql';
import { LocalStorageKeys } from '@shared/constants';
import { parsePhoneNumber } from '@shared/utils/phone-number';
import { useDebounce } from '@shared/hooks/use-debounce';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAvailableNumberSearch } from '@modules/settings/my-numbers/purchase-number/hooks/use-number-search';
import { PurchaseNumberEdge } from '@modules/settings/my-numbers/purchase-number/purchase-number.types';
import { useModal } from '@shared/hooks/use-modal';
import { useClaimFreeNumbers } from './use-claim-free-numbers';
import { useBundleStatusForClaim } from './use-bundle-status-for-claim';
import { claimNumbersModalStore } from '../store/claim-numbers-modal.store';
import { buildClaimCartNumbers, ClaimNumberSelection } from '../utils/build-claim-cart-numbers';
import { claimNumbersSchema, ClaimNumbersSchema } from '../schema/claim-numbers.schema';
import { ClaimNumbersModalProps } from '../claim-numbers.types';
import { buildDefaultValues } from '../utils/build-default-values';
import { useEncryptedParams } from '@shared/hooks/use-encrypted-params';

// US/CA local numbers are 10 digits — used to pad partial searches with wildcards (see handleSearch).
const NUMBER_LENGTH_MAX = 10;

export function useClaimNumbersForm({ availableRewardCount, prefill, onClaimed }: ClaimNumbersModalProps) {
  const { t } = useTranslation();
  // `matchTo` = where the typed digits must appear in the number: 'any' | 'first' | 'last'.
  const [matchTo, setMatchTo] = useState('any');
  const [params, setParams] = useEncryptedParams();
  // The raw digits the user typed, kept separately so re-applying match-to doesn't need a re-type.
  const [searchedTerm, setSearchedTerm] = useState('');
  // Full number detail (price, addressRequirements, etc.) for each picked id, keyed by phone number.
  // The form field array only stores {phoneNumber, friendlyName}; this map preserves the rest for submit.
  const [selectedRows, setSelectedRows] = useState<Record<string, PurchaseNumberEdge>>({});
  // The actual search string sent to the API — the typed digits plus any wildcard padding.
  const [contains, setContains] = useState((prefill?.phoneNumber as string) ?? '');

  const navigate = useNavigate();
  const claimModal = claimNumbersModalStore();

  const numberClaimForm = useForm<ClaimNumbersSchema>({
    mode: 'onChange',
    defaultValues: buildDefaultValues(prefill),
    // the max-selectable rule depends on the reward cap, so the schema is built per render
    resolver: zodResolver(
      claimNumbersSchema(availableRewardCount, (phoneNumber) => selectedRows[phoneNumber]?.isoCountry ?? undefined, t)
    ),
  });

  // One editable row per selected number (drives the friendly-name inputs); kept in sync in handleNumbersChange.
  const { fields, append, remove, replace } = useFieldArray({
    control: numberClaimForm.control,
    name: 'numbers',
    shouldUnregister: true,
  });

  // Tracks the isoCountry/numberType the clear-on-change effect below last saw, so it can tell
  // a user-driven change apart from a prefill programmatically applying its own country/type.
  const prevCountryRef = useRef<string | undefined>(prefill?.isoCountry ?? 'CA');
  const prevNumberTypeRef = useRef<NumberTypeEnum | undefined>(
    (prefill?.numberType as NumberTypeEnum) ?? NumberTypeEnum.local
  );

  useEffect(() => {
    if (!prefill?.phoneNumber) return;
    numberClaimForm.reset(buildDefaultValues(prefill));
    setContains(prefill.phoneNumber);
    setSelectedRows((prev) => ({ ...prev, [prefill.phoneNumber as string]: prefill }));
    // Sync immediately (not "skip the next clear-effect run") — this way a prefill whose
    // country/type happen to match the current defaults doesn't leave a stale skip flag armed
    // to wrongly swallow the next real user-driven change.
    prevCountryRef.current = (prefill.isoCountry as string) ?? prevCountryRef.current;
    prevNumberTypeRef.current = (prefill.numberType as NumberTypeEnum) ?? prevNumberTypeRef.current;
  }, [prefill]);

  // Lazy re-fetch of the user/workspace after a successful claim, to refresh number counts.
  const workspaceAuthMeV2Result = useworkspaceAuthMeV2LazyQuery();

  // Wait 400ms after typing stops before hitting the search API.
  const debouncedContains = useDebounce(contains, 400);

  const selectedCapabilities = numberClaimForm.watch('capabilities');
  const selectedCountry = numberClaimForm.watch('isoCountry');
  const selectedNumberType = numberClaimForm.watch('numberType');
  const isGB = selectedCountry === 'GB';

  // Turn the checkbox capabilities (['voice','SMS','MMS']) into the boolean flags the search query expects.
  function createGqlVariables() {
    const obj: { voiceEnabled?: boolean; smsEnabled?: boolean; mmsEnabled?: boolean } = {};

    (selectedCapabilities as string[])?.forEach((value) => {
      if (value === 'voice') obj.voiceEnabled = true;
      if (value === 'SMS') obj.smsEnabled = true;
      if (value === 'MMS') obj.mmsEnabled = true;
    });

    return obj;
  }

  const numberSearchResult = useAvailableNumberSearch({
    countryCode: selectedCountry,
    numberType: isGB ? (selectedNumberType as NumberTypeEnum) : NumberTypeEnum.local,
    ...(debouncedContains && { contains: debouncedContains }),
    ...createGqlVariables(),
  });

  // Closes the mixed-country/mixed-type gap: previously, switching isoCountry or numberType
  // re-ran the search but left prior selections in `fields`/`selectedRows`, so a batch could
  // end up spanning two countries or two types.
  useEffect(() => {
    const countryChanged = prevCountryRef.current !== selectedCountry;
    const numberTypeChanged = prevNumberTypeRef.current !== selectedNumberType;

    if (countryChanged || numberTypeChanged) {
      replace([]);
      setSelectedRows({});
      setSearchedTerm('');
      setContains('');
    }

    // GB defaults to mobile rather than inheriting whatever type was previously selected
    // (or the CA/US-only "local" default) — every other country's behavior is unchanged.
    if (countryChanged && selectedCountry === 'GB' && selectedNumberType !== NumberTypeEnum.mobile) {
      numberClaimForm.setValue('numberType', NumberTypeEnum.mobile);
      prevNumberTypeRef.current = NumberTypeEnum.mobile;
    } else {
      prevNumberTypeRef.current = selectedNumberType;
    }

    prevCountryRef.current = selectedCountry;
  }, [selectedCountry, selectedNumberType]);

  // Called on every keystroke in the number search box.
  const handleSearch = (inputText: string) => {
    // Normalize whatever was typed down to bare alphanumerics (strip +, spaces, dashes, etc.).
    const number = parsePhoneNumber(inputText) ?? {};
    const formattedTerm = (number || inputText)?.toString().replace(/[^A-Z0-9]/gi, '');
    // Too short to be meaningful — clear the filter so the API returns unfiltered results.
    if (formattedTerm.length < 2) {
      setContains('');
      return;
    }

    // Pad the term with '*' wildcards to 10 digits so the API can anchor the match:
    //   'first' → digits then wildcards (12*******), 'last' → wildcards then digits (*******12), 'any' → no padding.
    let result = formattedTerm;
    const searchTermLength = formattedTerm?.length ?? 0;
    const LengthDiff = NUMBER_LENGTH_MAX - searchTermLength;
    const repeatCount = searchTermLength >= NUMBER_LENGTH_MAX ? 0 : LengthDiff;
    const remaining = '*'.repeat(repeatCount);
    if (matchTo === 'first' && formattedTerm) result = formattedTerm + remaining;
    if (matchTo === 'last' && formattedTerm) result = remaining + formattedTerm;
    setSearchedTerm(formattedTerm);
    setContains(result);
  };

  // Called when the user chooses to claim later — remembers the choice so the modal isn't auto-opened again.
  // Also used for the modal's cross button and overlay click, since both should behave the same as claiming later.
  const onClaimLater = () => {
    localStorage.setItem(LocalStorageKeys.claimNumberSkipped, 'true');
    claimModal?.handleClose();
    params.delete('websiteNumberPurchaseType');
    setParams();
  };

  const { claim, loading: claimLoading } = useClaimFreeNumbers({
    onSuccess: (message) => {
      notification.success({ message });
      workspaceAuthMeV2Result?.[0]?.(); // refresh workspace/number counts
      claimModal?.handleClose();
      localStorage.setItem(LocalStorageKeys.claimNumberSkipped, 'true'); // also skip the modal for next refresh
      numberSearchResult?.refetch();
      onClaimed(); // clears the website-purchase prefill so a later reopen starts fresh
      navigate('/settings/my-numbers');
    },
    onError: (message) => notification.error({ message }),
  });

  // Bundle-verification branch: GB claims whose (isoCountry, numberType) pair isn't yet
  // bundle-approved must go through document collection before claiming — same conditional
  // logic the paid purchase flow uses (see deriveRegulatoryComplianceFieldRequirements).
  const bundleVerificationModal = useModal();
  const { checkBundleStatus } = useBundleStatusForClaim();
  const [pendingBundleSelection, setPendingBundleSelection] = useState<ClaimNumberSelection[] | null>(null);
  const [pendingCapabilities, setPendingCapabilities] = useState<string[]>([]);
  const [bundleFieldRequirements, setBundleFieldRequirements] = useState<{
    isIdentityFieldRequired: boolean;
    isAddressFieldRequired: boolean;
  } | null>(null);
  // Covers the GB-only bundle-status check, which sits between form submit and the actual claim —
  // claimLoading alone only reflects the claim mutation, so the button would show no feedback
  // while this network round trip is in flight.
  const [isCheckingBundleStatus, setIsCheckingBundleStatus] = useState(false);

  // Runs after the schema validates. Reunites each selected number with its full detail row and fires the claim
  // (US/CA) or, for a GB pair that isn't yet bundle-approved, opens the bundle-verification modal instead.
  const handleSubmit = async (data: ClaimNumbersSchema) => {
    const selections: ClaimNumberSelection[] = data.numbers.map((entry) => ({
      phoneNumber: entry.phoneNumber,
      friendlyName: entry.friendlyName,
      // Prefer the cached row from selectedRows; fall back to the current search results if it's still there.
      row: (selectedRows[entry.phoneNumber] ??
        numberSearchResult?.phoneNumbers?.find((num) => num.id === entry.phoneNumber)?.row) as PurchaseNumberEdge,
    }));

    // Guard: a row can go missing if search results changed between selection and submit — the claim needs
    // numberType + addressRequirements, so bail rather than send an incomplete cart.
    const hasInvalid = selections.some(
      (selection) => selection.row?.numberType == undefined || selection.row?.addressRequirements == undefined
    );
    if (hasInvalid) {
      return notification?.warning({ message: 'Invalid form data.' });
    }

    if (data.isoCountry !== 'GB') {
      return claim(buildClaimCartNumbers(selections, (data.capabilities as string[]) ?? []));
    }

    try {
      setIsCheckingBundleStatus(true);
      const firstRow = selections[0]?.row;
      const status = await checkBundleStatus({
        isoCountry: data.isoCountry,
        numberType: firstRow?.numberType as NumberTypeEnum,
        addressRequirements: firstRow?.addressRequirements ?? '',
      });

      if (status.isApproved) {
        return claim(buildClaimCartNumbers(selections, (data.capabilities as string[]) ?? []));
      }

      setPendingBundleSelection(selections);
      setPendingCapabilities((data.capabilities as string[]) ?? []);
      setBundleFieldRequirements({
        isIdentityFieldRequired: status.isIdentityFieldRequired,
        isAddressFieldRequired: status.isAddressFieldRequired,
      });
      bundleVerificationModal.handleOpen();
    } finally {
      setIsCheckingBundleStatus(false);
    }
  };

  // Fires when the user toggles the match-to (any/first/last) dropdown. Re-pads the *existing* typed term
  // with wildcards (same logic as handleSearch) so results update without the user retyping.
  const handleMatch = (align: string) => {
    numberClaimForm?.setValue('matchTo', align);
    let result = searchedTerm;
    const searchTermLength = searchedTerm?.length ?? 0;
    const LengthDiff = NUMBER_LENGTH_MAX - searchTermLength;
    const repeatCount = searchTermLength >= NUMBER_LENGTH_MAX ? 0 : LengthDiff;
    const remaining = '*'.repeat(repeatCount);
    if (align === 'first' && searchedTerm) result = searchedTerm + remaining;
    if (align === 'last' && searchedTerm) result = remaining + searchedTerm;
    setContains(result);
    setMatchTo(align);
  };

  // The Ant multi-select gives us the full list of currently-selected ids on every change.
  // We diff it against the form's field array to add/remove rows, and keep selectedRows in sync.
  const handleNumbersChange = (ids: string[]) => {
    const currentIds = fields.map((field) => field.phoneNumber);
    const newlyAddedIds = ids.filter((id) => !currentIds.includes(id));

    // append newly-selected numbers
    newlyAddedIds.forEach((id) => append({ phoneNumber: id, friendlyName: '' }));

    // remove de-selected numbers (iterate from the end to keep indexes valid)
    fields
      .map((field, index) => ({ field, index }))
      .filter(({ field }) => !ids.includes(field.phoneNumber))
      .sort((a, b) => b.index - a.index)
      .forEach(({ index }) => remove(index));

    // keep the detail map in sync with the current selection
    setSelectedRows((prev) => {
      const next: Record<string, PurchaseNumberEdge> = {};
      ids.forEach((id) => {
        const row =
          prev[id] ?? (numberSearchResult?.phoneNumbers?.find((num) => num.id === id)?.row as PurchaseNumberEdge);
        if (row) next[id] = row;
      });
      return next;
    });

    // Picking a number clears the search filter so the dropdown shows the full list again on next open,
    // instead of staying stuck on the last-typed filter.
    if (newlyAddedIds.length > 0) {
      setSearchedTerm('');
      setContains('');
    }
  };

  // Ids of everything currently selected — feeds back into the multi-select's `value`.
  const selectedIds = fields.map((field) => field.phoneNumber);
  // Top-level validation message for the numbers array (e.g. "Choose at least one", "at most N").
  // Suppressed until the first submit attempt so it doesn't fire the moment another field changes
  // (mode: 'onChange' re-validates the whole schema on every change event) — every other field's
  // error still shows live, unaffected by this gate.
  const numbersError = numberClaimForm.formState.isSubmitted
    ? numberClaimForm.formState.errors.numbers?.message
    : undefined;

  // Runs once the bundle-verification modal successfully requests regulatory compliance —
  // closes both modals and sends the user to the Pending tab to track the request.
  const onBundleRequested = () => {
    claimModal?.handleClose();
    navigate('/settings/my-numbers?status=pending');
  };

  // Everything the modal component (ClaimNumbersModal) needs to render and drive the form.
  return {
    availableRewardCount,
    numberClaimForm,
    numberSearchResult,
    fields,
    selectedIds,
    numbersError,
    claimLoading: claimLoading || isCheckingBundleStatus,
    handleSearch,
    handleMatch,
    handleNumbersChange,
    handleSubmit,
    onClaimLater,
    bundleVerificationModal,
    pendingBundleSelection,
    pendingCapabilities,
    bundleFieldRequirements,
    onBundleRequested,
  };
}
